import React, { useState, useCallback } from 'react';
import { AuditTrailQuery } from '../../queries/fetch-entitlement-audit-trail';
import { FormattedDate, DynamicResultTable, DateFilter, NoDataContentInTable } from '@css-xp/base';
import { useQuery } from '@apollo/client';
import {
  FetchAuditTrailForEntitlementQuery,
  FetchAuditTrailForEntitlementQueryVariables,
  SortDirection,
} from '@css-xp/graphtypes/src/generated-types-new';
import { memoize } from 'lodash';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
export interface AuditTrailTabProps {
  id: string;
  showFilters?: boolean;
}

export const searchOption = {
  sort: {
    sortFields: ['created', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

export const filterOptions = [
  {
    key: 'idText',
    value: 'transaction-account',
  },
];

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="pv-8">{node}</div>;
};

const AuditTrailTab = function ({ id, showFilters = false }: AuditTrailTabProps) {
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);

  const defaultSearchOption = {
    ...searchOption,
    first: rowsPerPage,
  };

  const { data, loading, refetch } = useQuery<
    FetchAuditTrailForEntitlementQuery,
    FetchAuditTrailForEntitlementQueryVariables
  >(AuditTrailQuery, {
    variables: {
      id: id,
      dateFilter: dateRange,
      searchOptions: defaultSearchOption,
      filterOptions,
    },
    displayName: 'fetchEntitlementAuditTrail',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const getDateRange = (value: any) => {
    setDateRange(value);
  };

  const getColumns = memoize(() => {
    const columns = [
      {
        content: 'Date',
        width: 15,
      },
      {
        content: 'Details',
        width: 30,
      },
      {
        content: 'Actioned by',
        width: 15,
      },
    ];
    return {
      cells: columns,
    };
  });

  const memoizedCreateRows = () => {
    return (auditTrailNodes ?? []).map(
      //@ts-ignore
      (audit: any, index: number) => {
        const { created, body, contactKey, author } = audit;
        const action = body?.split('-');
        const update = action?.[0];
        return {
          key: index.toString(),
          cells: [
            {
              content: wrapInCell(created ? <FormattedDate date={created} showTime /> : <NoDataContentInTable />),
            },
            {
              content: wrapInCell(body || contactKey ? [body, contactKey].join(' ') : <NoDataContentInTable />),
            },
            {
              content: wrapInCell(author ? author : created && update ? 'System' : <NoDataContentInTable />),
            },
          ],
        };
      }
    );
  };

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          ...defaultSearchOption,
          offset: (pageNumber - 1) * rowsPerPage,
          first: rowsPerPage,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const emptyStateOrderProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  const queryResults = data?.entitlementDetails.auditNotes;
  const auditTrailNodes = queryResults?.nodes;
  const totalCount = queryResults?.totalCount || 0;

  return (
    <div className="statuspage-tab" data-testid="entitlement-audit-trail">
      <DynamicResultTable
        headingText="Account audit trail"
        head={getColumns()}
        rows={memoizedCreateRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={auditTrailNodes && auditTrailNodes.length > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        showFilters={showFilters}
      >
        <DateFilter dateValue={getDateRange} />
      </DynamicResultTable>
    </div>
  );
};

export default AuditTrailTab;
